import React from 'react';
import { styled } from '@mui/system';
import Box from '@mui/material/Box';

import LocalOfferIconMessage from 'bento-ordering/base/components/LocalOfferIconMessage';
import useUnlockedPromotions from 'bento-ordering/promotions/hooks/useUnlockedPromotions';
import useClosestPromotionToUnlock from 'bento-ordering/promotions/hooks/useClosestPromotionToUnlock';
import useMarketedPromotions from 'bento-ordering/promotions/hooks/useMarketedPromotions';
import {
  getQualifyingItemMessage,
  getRewardItemMessage
} from 'bento-ordering/promotions/hooks/usePromotionProgressCartUpdateMesssage';
import { formatSentenceWithAVsAn } from 'bento-ordering/utils/formatSentenceWithAVsAn';
import { promotionTypes } from 'constants/promotions';

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start'
}));

function CartDrawerPromotions() {
  const unlockedPromotions = useUnlockedPromotions();
  const closestPromotionToUnlock = useClosestPromotionToUnlock();
  const allPromotions = useMarketedPromotions();
  const qualifyingItemName =
    closestPromotionToUnlock?.promotion?.qualifying_item_details.length === 1 &&
    closestPromotionToUnlock?.promotion?.qualifying_item_details[0].name;
  const rewardItemName =
    closestPromotionToUnlock?.promotion?.product_promotion_details.length ===
      1 &&
    closestPromotionToUnlock?.promotion?.product_promotion_details[0].name;

  const getSingleUnlockedPromotionText = () => {
    let promotionMessage;
    const unlockedPromotion = unlockedPromotions[0].promotion;
    if (unlockedPromotion.promotion_type === promotionTypes.FREE_ITEM) {
      if (unlockedPromotion.product_promotion_details.length === 1) {
        const unlockedPromotionProductName =
          unlockedPromotion.product_promotion_details[0].name;
        promotionMessage = `FREE ${unlockedPromotionProductName} unlocked!`;
      } else if (unlockedPromotion.product_promotion_details.length > 1) {
        promotionMessage = 'FREE items unlocked!';
      }
    } else if (
      unlockedPromotion.promotion_type === promotionTypes.SPECIAL_ITEM_PRICING
    ) {
      if (unlockedPromotion.product_promotion_details.length === 1) {
        const unlockedPromotionProductName =
          unlockedPromotion.product_promotion_details[0].name;
        promotionMessage = `$${unlockedPromotion.special_item_price} ${unlockedPromotionProductName} unlocked!`;
      } else if (unlockedPromotion.product_promotion_details.length > 1) {
        promotionMessage = 'Discounted items unlocked!';
      }
    } else if (
      (unlockedPromotion.promotion_type === promotionTypes.FIXED_AMOUNT &&
        unlockedPromotion.product_promotion_details.length > 1) ||
      (unlockedPromotion.promotion_type === promotionTypes.PERCENT_AMOUNT &&
        unlockedPromotion.product_promotion_details.length > 1)
    ) {
      promotionMessage = 'Discounted items unlocked!';
    } else {
      promotionMessage = `${unlockedPromotion.name} unlocked!`;
    }
    return promotionMessage;
  };

  const getPromotionText = () => {
    if (unlockedPromotions.length === 1 && allPromotions.length === 1) {
      return getSingleUnlockedPromotionText();
    } else if (unlockedPromotions.length === 1) {
      return '1 promotion unlocked!';
    } else if (unlockedPromotions.length === 0) {
      return '';
    } else {
      return `${unlockedPromotions.length} promotions unlocked!`;
    }
  };

  const getClosestPromotionToUnlock = () => {
    if (!closestPromotionToUnlock) {
      return '';
    }

    let promotionMessage = '';
    if (
      closestPromotionToUnlock.promotion.qualifying_item_details.length >= 1
    ) {
      promotionMessage = getQualifyingItemMessage(
        closestPromotionToUnlock,
        qualifyingItemName,
        rewardItemName
      );
    } else if (
      closestPromotionToUnlock.promotion.product_promotion_details.length >= 1
    ) {
      promotionMessage = getRewardItemMessage(
        closestPromotionToUnlock,
        rewardItemName
      );
    } else {
      promotionMessage = `$${closestPromotionToUnlock.amount_before_unlocked} more to unlock ${closestPromotionToUnlock.promotion.name}.`;
    }
    const formattedPromotionMessage = formatSentenceWithAVsAn(promotionMessage);
    return formattedPromotionMessage;
  };

  const concatPromoMessage = `${getPromotionText()} ${getClosestPromotionToUnlock()}`;

  return (
    <StyledBox>
      <LocalOfferIconMessage message={concatPromoMessage} />
    </StyledBox>
  );
}

export default CartDrawerPromotions;
